import { type FlatSurface, flattenPolygonRing, type ShapeVertex } from '@repo/api-contracts';
import type { PointerEvent, ReactElement } from 'react';
import { type PhotoDimensions, surfaceMmToPhoto, type VertexHandle } from '@/entities/edit-area';
import { closedPathOf } from '../model/svg-path';

interface PointEditOverlayProps {
  /** 바깥 고리 점(표면 mm, 곡선 손잡이 포함). */
  readonly points: readonly ShapeVertex[];
  readonly surface: FlatSurface;
  readonly photoSize: PhotoDimensions;
  readonly selectedIndex: number | null;
  /** 화면에서 잡을 수 있는 최소 한 변(사진 px). */
  readonly minSizePx: number;
  readonly onVertexStart: (event: PointerEvent<SVGElement>, index: number) => void;
  readonly onHandleStart: (
    event: PointerEvent<SVGElement>,
    index: number,
    which: VertexHandle,
  ) => void;
  readonly onRingPointerDown: (event: PointerEvent<SVGElement>) => void;
}

const COLOR = '#2563eb';
/** 선 위 누르기를 받는 투명 테두리의 화면 두께(px). */
const RING_HIT_SCREEN_PX = 10;
const HANDLES: readonly VertexHandle[] = ['handleIn', 'handleOut'];

/**
 * 점 편집 화면 — 풀어 쓴 바깥 고리를 진하게, 점마다 네모 핸들(고른 점은 채움)을, 손잡이가 있는 점은 점→손잡이 가는 선과
 * 끝의 작은 원을 그린다. 고리 위 투명한 굵은 테두리가 선 위 누르기(점 더하기)를 받는다.
 */
export const PointEditOverlay = ({
  points,
  surface,
  photoSize,
  selectedIndex,
  minSizePx,
  onVertexStart,
  onHandleStart,
  onRingPointerDown,
}: PointEditOverlayProps): ReactElement => {
  const toPhoto = (point: { xMm: number; yMm: number }) =>
    surfaceMmToPhoto(surface, photoSize, { xMm: point.xMm, yMm: point.yMm });
  const ring = closedPathOf(flattenPolygonRing(points).map(toPhoto));
  const size = Math.max(8, minSizePx, Math.max(photoSize.widthPx, photoSize.heightPx) * 0.006);
  const radius = size * 0.4;
  return (
    <g data-testid="point-edit-overlay">
      <path
        data-testid="point-edit-ring"
        d={ring}
        fill="none"
        stroke="transparent"
        strokeWidth={RING_HIT_SCREEN_PX}
        vectorEffect="non-scaling-stroke"
        pointerEvents="stroke"
        style={{ cursor: 'copy' }}
        onPointerDown={onRingPointerDown}
      >
        <title>선 위를 누르면 점을 더합니다</title>
      </path>
      <path
        d={ring}
        fill="none"
        stroke={COLOR}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
        pointerEvents="none"
      />
      {points.map((vertex, index) => {
        const at = toPhoto(vertex);
        return HANDLES.map((which) => {
          const handle = vertex[which];
          if (handle === undefined) return null;
          const end = toPhoto(handle);
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: 점은 순서가 곧 정체성이다.
            <g key={`${index}-${which}`}>
              <line
                x1={at.x}
                y1={at.y}
                x2={end.x}
                y2={end.y}
                stroke={COLOR}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
              <circle
                data-testid={`point-edit-handle-${index}-${which === 'handleIn' ? 'in' : 'out'}`}
                cx={end.x}
                cy={end.y}
                r={radius}
                fill="white"
                stroke={COLOR}
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
                pointerEvents="all"
                style={{ cursor: 'move' }}
                onPointerDown={(event) => onHandleStart(event, index, which)}
              />
            </g>
          );
        });
      })}
      {points.map((vertex, index) => {
        const at = toPhoto(vertex);
        const selected = index === selectedIndex;
        return (
          // biome-ignore lint/a11y/useSemanticElements: SVG 안에는 HTML button 을 둘 수 없다. 점은 포인터로만 고친다.
          <rect
            // biome-ignore lint/suspicious/noArrayIndexKey: 점은 순서가 곧 정체성이다(같은 자리 점도 있을 수 있다).
            key={index}
            role="button"
            aria-label={`점 ${index + 1}${selected ? ' (고름)' : ''}`}
            data-testid={`point-edit-vertex-${index}`}
            data-selected={selected ? 'true' : 'false'}
            x={at.x - size / 2}
            y={at.y - size / 2}
            width={size}
            height={size}
            fill={selected ? COLOR : 'white'}
            stroke={COLOR}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            pointerEvents="all"
            style={{ cursor: 'move' }}
            onPointerDown={(event) => onVertexStart(event, index)}
          />
        );
      })}
    </g>
  );
};
